import { SubscribeToAlertsMutationPayload } from "app/pages/activation-alerts/queries/alerts/useSubscribeToAlertsMutation";
import { updateBooleanInActivationItem } from "app/pages/activation/helpers/home/updateBooleanInActivationItem";
import type { SyntheticEvent } from "react";

import { UseMutateFunction, useQueryClient } from "@tanstack/react-query";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import ConfirmDefaultActivationModal from "app/pages/activation/components/ConfirmDefaultActivationModal/ConfirmDefaultActivationModal";
import { ONE } from "app/pages/activation/components/Header/constants";
import { TOOLTIP_DELAY } from "app/pages/activation/components/ProjectsList/constants";
import { SharedIcon } from "app/pages/activation/components/Shared/SharedIcon/SharedIcon";
import { ActivationComponentAssets } from "app/pages/activation/helpers/ActivationAssets";
import { prepareTooltip } from "app/pages/activation/helpers/home/text";
import useDefaultActivation from "app/pages/activation/hooks/settings/useDefaultActivation";
import { TABS_ARRAY } from "app/pages/activation/modules/Settings/config";
import { SettingsTabs } from "app/pages/activation/modules/Settings/types";
import { ACTIVATION_METADATA } from "app/pages/activation/queries/data/useActivationMetadataQuery";
import { ACTIVATION_OBJECTS_QUERY_KEY } from "app/pages/activation/queries/data/useActivationSetListQuery";
import { ACTIVATION_SETTINGS_ROUTE, DEFAULT_ACTIVATION_GRANULARITY } from "app/pages/activation/routing/constants";
import { activationTracking } from "app/pages/activation/services/tracking";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import CountryService from "app/services/CountryService";

import { TopicName } from "../../../../Topics/Topic/TopicName/TopicName";

import * as SC from "./styles";
import {
    COMPETITORS,
    DEFAULT_TOOLTIP,
    SUBSCRIBE_TOOLTIP,
    UNSET_DEFAULT_TOOLTIP,
    SUBSCRIBE_SETTINGS_TOOLTIP,
} from "./texts";

type NameSectionSchema = {
    isLoading: boolean;
    name: string;
    domain: string;
    country: number;
    competitors: string[];
    sharedBy: string;
    activationId: string;
    isSubscribed: boolean;
    isFavorite: boolean;
    isGaConnected?: boolean;
    onSubscribe:UseMutateFunction<void, Error, SubscribeToAlertsMutationPayload>;
    isOwn: boolean;
};

export const NameSection = ({
    isLoading,
    name,
    domain,
    country,
    competitors,
    sharedBy,
    activationId,
    isSubscribed,
    isFavorite,
    isGaConnected,
    isOwn,
    onSubscribe,
}: NameSectionSchema) => {
    const countryObject = CountryService.getCountryById(country);
    const track = activationTracking.homepage;
    const queryClient = useQueryClient();

    const {
        isConfirmModalOpen,
        isLoading: isDefaultActivationLoading,
        updateDefaultActivation,
        toggleDefaultActivation,
        setIsConfirmModalOpen,
    } = useDefaultActivation();
    const isMultiProjects = ActivationComponentAssets.isMultiProjects;

    const onCloseClick = () => {
        setIsConfirmModalOpen(false);
    };

    const invalidateQueries = () => {
        void queryClient.invalidateQueries({ queryKey: [ACTIVATION_METADATA], type: "all" });
        void queryClient.invalidateQueries({ queryKey: [ACTIVATION_OBJECTS_QUERY_KEY], type: "all" });
    };

    const onClickSetDefault = async (event: SyntheticEvent<HTMLSpanElement>) => {
        event.stopPropagation();

        if (isFavorite) {
            track.trackRemoveDefaultProject();
        } else {
            track.trackChangeDefaultProject();
        }

        await toggleDefaultActivation(activationId, invalidateQueries, false);
    };

    const onUpdateDefaultActivation = async () => {
        await updateDefaultActivation(invalidateQueries, false);
    };

    const onClickSubscribe = (event: SyntheticEvent<HTMLSpanElement>) => {
        event.stopPropagation();
        track.trackSubscribe("table");

        if (isSubscribed) {
            track.trackSubscriptionSettingsIcon();

            return proNavigator.go(ACTIVATION_SETTINGS_ROUTE, {
                domain,
                key: activationId,
                country,
                webSource: ActivationComponentAssets.defaultSource,
                timeGranularity: DEFAULT_ACTIVATION_GRANULARITY,
                tab: TABS_ARRAY.indexOf(SettingsTabs.Subscriptions),
            });
        }

        onSubscribe(
            { granularity: DEFAULT_ACTIVATION_GRANULARITY },
            {
                onSuccess: () => {
                    updateBooleanInActivationItem({ queryClient, itemId: activationId, objectKey: "isSubscribed" });
                },
            },
        );
    };

    const tooltipText = isSubscribed ? translate(SUBSCRIBE_SETTINGS_TOOLTIP) : translate(SUBSCRIBE_TOOLTIP);

    return (
        <>
            <ConfirmDefaultActivationModal
                isOpen={isConfirmModalOpen}
                onUpdateDefaultActivation={onUpdateDefaultActivation}
                onCloseClick={onCloseClick}
                isLoading={isDefaultActivationLoading}
                selectedName={name}
            />

            <SC.NameContainer>
                <SC.NameRow>
                    <TopicName
                        name={name}
                        isLoading={isLoading}
                    />
                    {isGaConnected ? (
                        <SWReactIcons
                            iconName="ga-icon"
                            size="xs"
                        />
                    ) : null}

                    {!isOwn ? (
                        <SharedIcon
                            className="customIcon"
                            sharedBy={sharedBy}
                            size="xs"
                        />
                    ) : null}

                    {isMultiProjects ? (
                        <Tooltip
                            content={translate(isFavorite ? UNSET_DEFAULT_TOOLTIP : DEFAULT_TOOLTIP)}
                            delay={{ open: TOOLTIP_DELAY }}
                        >
                            <span onClick={onClickSetDefault}>
                                <SC.CustomIcon
                                    iconName={isFavorite ? "star" : "star-outline"}
                                    size="xs"
                                    iconColor={colorsPalettes.blue[400]}
                                    className="hidden-icon"
                                />
                            </span>
                        </Tooltip>
                    ) : null}

                    <Tooltip
                        content={tooltipText}
                        delay={{ open: TOOLTIP_DELAY }}
                    >
                        <span onClick={onClickSubscribe}>
                            <SWReactIcons
                                iconName={isSubscribed ? "email-subscribed-checked" : "email"}
                                size="xs"
                                className="hidden-icon"
                                iconColor={colorsPalettes.blue[400]}
                            />
                        </span>
                    </Tooltip>
                </SC.NameRow>

                <SC.Competitors>
                    {`${domain} vs.`}
                    {competitors.length > ONE ? (
                        <Tooltip
                            enabled={competitors.length > ONE}
                            content={<SC.TooltipBody>{prepareTooltip(competitors)}</SC.TooltipBody>}
                            delay={{ open: TOOLTIP_DELAY }}
                        >
                            <span>{`${competitors.length} ${translate(COMPETITORS)}`}</span>
                        </Tooltip>
                    ) : (
                        competitors[0]
                    )}
                    <SC.Separator>•</SC.Separator>
                    {countryObject?.text}
                </SC.Competitors>
            </SC.NameContainer>
        </>
    );
};

NameSection.displayName = "HomeNameSection";
